'use client';

import React, { useState, useMemo } from 'react';
import { MaterialReactTable } from 'material-react-table';
import { Box, Typography, Paper, Button, IconButton, Switch, FormControlLabel, Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';
import { useCtms, useUpdateCtmAsset } from '@/hooks/api/useCtms';
import { useLocations } from '@/hooks/useLocation';
import { orderBy } from 'lodash';
import { Download, Delete, Restore } from '@mui/icons-material';
import TitleBar from '../layout/TitleBar';
import { useNavigate } from 'react-router-dom';
import { useAuthData } from '@/hooks/getAuthData';
import { useNotification } from '@/contexts/NotificationContext';

const CtmPage: React.FC = () => {
  const navigate = useNavigate();
  const { roles } = useAuthData();
  const hasAdminRole = roles.some((x) => x.name === 'Admin');
  const { data: ctmsData } = useCtms();
  const { data: locationsData } = useLocations();
  const updateCtmAssetMutation = useUpdateCtmAsset();
  const { showSuccess, showError } = useNotification();

  const [showInactive, setShowInactive] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [assetToDelete, setAssetToDelete] = useState<any>(null);

  const allCtms = useMemo(() => {
    if (!ctmsData) return [];
    return Array.isArray(ctmsData) ? ctmsData : Object.values(ctmsData);
  }, [ctmsData]);

  // Filter out inactive assets by default
  const ctms = useMemo(() => {
    if (showInactive) {
      return allCtms;
    }
    return allCtms.filter((ctm: any) => ctm.active !== false);
  }, [allCtms, showInactive]);

  const locations = useMemo(() => {
    if (!locationsData) return [];
    const locs = Array.isArray(locationsData) ? locationsData : Object.values(locationsData);
    return orderBy(locs, ['name'], ['asc']);
  }, [locationsData]);
  // Generate Location Filter Options
  const locationOptions = locations.map((location: any) => ({
    value: location.id.toString(),
    label: location.name,
  }));

  const handleDelete = (ctm: any) => {
    setAssetToDelete(ctm);
    setDeleteConfirmOpen(true);
  };

  const handleReactivate = (ctm: any) => {
    setAssetToDelete(ctm);
    setDeleteConfirmOpen(true);
  };

  const confirmDelete = () => {
    if (assetToDelete) {
      const isReactivating = assetToDelete.active === false || assetToDelete.active === null;
      const newActiveValue = isReactivating ? true : false;
      
      console.log('Updating CTM asset:', {
        id: assetToDelete.id,
        currentActive: assetToDelete.active,
        isReactivating,
        newActiveValue
      });
      
      updateCtmAssetMutation.mutate({ id: assetToDelete.id, active: newActiveValue }, {
        onSuccess: () => {
          setDeleteConfirmOpen(false);
          setAssetToDelete(null);
          showSuccess(isReactivating ? 'CTM reactivated successfully' : 'CTM deactivated successfully');
        },
        onError: (error: any) => {
          showError(error.message || 'Failed to update CTM');
        },
      });
    }
  };

  // Column visibility state with localStorage persistence
  const getStoredColumnVisibility = () => {
    const stored = localStorage.getItem('ctmPage_columnVisibility');
    if (stored) {
      try {
        return JSON.parse(stored);
      } catch (e) {
        return null;
      }
    }
    return null;
  };

  const saveColumnVisibility = (visibility: Record<string, boolean>) => {
    localStorage.setItem('ctmPage_columnVisibility', JSON.stringify(visibility));
  };

  const defaultColumnVisibility = {
    assetId: true,
    locationId: true,
    active: false,
    id: false,
    position: false,
    createdAt: false,
    updatedAt: false,
  };

  const [columnVisibility, setColumnVisibility] = useState<Record<string, boolean>>(() => {
    const stored = getStoredColumnVisibility();
    return stored || defaultColumnVisibility;
  });

  const ctmColumns = [
    { accessorKey: 'assetId', header: 'CTM ID' },
    {
      accessorKey: 'locationId', header: 'Location',
      Cell: ({ row }: any) => {
        const location = locations.find((location) => location.id === row.original.locationId);
        return <span style={{ cursor: 'pointer', color: '#1976d2', textDecoration: 'underline' }} onClick={() => navigate(`/app/locations/${row.original.locationId}`)}>{location?.name || 'No name'}</span>
      },
      filterVariant: 'select', // Enables a dropdown filter
      filterSelectOptions: locationOptions, // Provide the dropdown options
    },
    {
      accessorKey: 'active', header: 'Active',
      Cell: ({ row }: any) => {
        return row.original.active !== false ? 'Yes' : 'No';
      },
      enableHiding: true,
    },
    {
      accessorKey: 'position', header: 'Position',
      Cell: ({ row }: any) => row.original.position || '-',
      enableHiding: true,
    },
    {
      accessorKey: 'id', header: 'ID',
      Cell: ({ row }: any) => row.original.id,
      enableHiding: true,
    },
    {
      accessorKey: 'createdAt', header: 'Created At',
      Cell: ({ row }: any) => row.original.createdAt ? new Date(row.original.createdAt).toLocaleDateString() : '-',
      enableHiding: true,
    },
    {
      accessorKey: 'updatedAt', header: 'Updated At',
      Cell: ({ row }: any) => row.original.updatedAt ? new Date(row.original.updatedAt).toLocaleDateString() : '-',
      enableHiding: true,
    },
    ...(hasAdminRole ? [{
      id: 'actions',
      header: 'Actions',
      Cell: ({ row }: any) => {
        const isInactive = row.original.active === false || row.original.active === null;
        return isInactive ? (
          <IconButton
            size="small"
            onClick={() => handleReactivate(row.original)}
            color="primary"
            title="Reactivate"
          >
            <Restore />
          </IconButton>
        ) : (
          <IconButton
            size="small"
            onClick={() => handleDelete(row.original)}
            color="error"
            title="Delete"
          >
            <Delete />
          </IconButton>
        );
      },
    }] : []),
  ];

  const breadcrumbs = [
    { label: 'Home', href: '/app/dashboard' },
    { label: 'CTMs' }, // No href for the current page
  ];

  const rightContent = (
    <>
      <Button
        variant="outlined"
        startIcon={<Download />}
        sx={{ textTransform: 'none' }}
      >
        Export
      </Button>
      <Button onClick={() => {
      }} variant="contained" sx={{ textTransform: 'none' }}>
        + Add CTM
      </Button>
    </>
  );

  return (
    <>
      <TitleBar
        breadcrumbs={breadcrumbs}
        title="CTMs"
        rightContent={rightContent}
      />

      <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-end' }}>
        <FormControlLabel
          control={
            <Switch
              checked={showInactive}
              onChange={(e) => setShowInactive(e.target.checked)}
            />
          }
          label="Show Inactive"
        />
      </Box>

      <MaterialReactTable 
        columns={ctmColumns as any} 
        data={ctms} 
        initialState={{ 
          density: 'compact',
          columnVisibility: columnVisibility
        }}
        state={{
          columnVisibility: columnVisibility
        }}
        onColumnVisibilityChange={(updater) => {
          const newVisibility = typeof updater === 'function' ? updater(columnVisibility) : updater;
          setColumnVisibility(newVisibility);
          saveColumnVisibility(newVisibility);
        }}
      />

      <Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)}>
        <DialogTitle>
          {(assetToDelete?.active === false || assetToDelete?.active === null) ? 'Confirm Reactivate' : 'Confirm Delete'}
        </DialogTitle>
        <DialogContent>
          <Typography>
            {(assetToDelete?.active === false || assetToDelete?.active === null) ? (
              <>Are you sure you want to reactivate CTM <strong>{assetToDelete?.assetId || assetToDelete?.id}</strong>?</>
            ) : (
              <>Are you sure you want to delete CTM <strong>{assetToDelete?.assetId || assetToDelete?.id}</strong>? 
              This will set it as inactive.</>
            )}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)}>Cancel</Button>
          <Button 
            onClick={confirmDelete} 
            variant="contained" 
            color={(assetToDelete?.active === false || assetToDelete?.active === null) ? "primary" : "error"}
          >
            {(assetToDelete?.active === false || assetToDelete?.active === null) ? 'Reactivate' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default CtmPage;
